import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcons } from '../../icons/icons';

interface MoveDropdownProps {
  options: string[];
  onSelect: (option: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  triggerClassName?: string;
}

const getStatusBadge = (option: string) => {
  const normalized = option.toLowerCase();
  if (normalized.includes('outstanding')) {
    return { dot: 'bg-purple-500 ring-purple-100', text: 'text-purple-600' };
  }
  if (normalized.includes('assessment')) {
    return { dot: 'bg-amber-500 ring-amber-100', text: 'text-amber-600' };
  }
  if (normalized.includes('shortlisted')) {
    return { dot: 'bg-emerald-500 ring-emerald-100', text: 'text-emerald-600' };
  }
  if (normalized.includes('suitable')) {
    return { dot: 'bg-blue-500 ring-blue-100', text: 'text-blue-600' };
  }
  if (normalized.includes('unsuccessful')) {
    return { dot: 'bg-rose-500 ring-rose-100', text: 'text-rose-600' };
  }
  if (normalized.includes('new')) {
    return { dot: 'bg-cyan-500 ring-cyan-100', text: 'text-cyan-600' };
  }
  if (normalized.includes('psychometric')) {
    return { dot: 'bg-violet-500 ring-violet-100', text: 'text-violet-600' };
  }
  if (normalized.includes('wellness')) {
    return { dot: 'bg-teal-500 ring-teal-100', text: 'text-teal-600' };
  }
  return { dot: 'bg-slate-400 ring-slate-100', text: 'text-slate-600' };
};

export const MoveDropdown = ({
  options,
  onSelect,
  isOpen,
  onToggle,
  triggerLabel = 'Move',
  triggerIcon,
  triggerClassName,
}: MoveDropdownProps) => {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  }>({
    top: 0,
    left: 0,
    maxHeight: 360,
    placement: 'bottom',
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 250;
    const GAP = 6;
    const PADDING = 12;

    const measuredHeight = menuRef.current
      ? menuRef.current.offsetHeight
      : Math.min(options.length * 42 + 36, 360);

    const spaceBelow = window.innerHeight - rect.bottom - GAP - PADDING;
    const spaceAbove = rect.top - GAP - PADDING;

    const shouldFlip = spaceBelow < measuredHeight && spaceAbove > spaceBelow;
    const placement: 'top' | 'bottom' = shouldFlip ? 'top' : 'bottom';

    let maxHeight = 360;
    let top = 0;

    if (placement === 'top') {
      maxHeight = Math.max(120, Math.min(360, spaceAbove));
      const effectiveHeight = Math.min(measuredHeight, maxHeight);
      top = Math.max(PADDING, rect.top - effectiveHeight - GAP);
    } else {
      maxHeight = Math.max(120, Math.min(360, spaceBelow));
      top = rect.bottom + GAP;
    }

    // Align right edge of menu to right edge of button
    let left = rect.right - menuWidth;
    if (left + menuWidth > window.innerWidth - PADDING) {
      left = window.innerWidth - menuWidth - PADDING;
    }
    if (left < PADDING) {
      left = PADDING;
    }

    setPosition({ top, left, maxHeight, placement });
  }, [options.length]);

  useLayoutEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    calculatePosition();

    const handleScrollOrResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        onToggle();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, calculatePosition, onToggle]);

  return (
    <>
      <button
          ref={buttonRef}
          onClick={onToggle}
          type='button'
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all duration-150 shadow-sm active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
            isOpen
              ? 'bg-slate-100 border-slate-300 text-slate-900 ring-2 ring-blue-500/20'
              : 'bg-white border-slate-200/90 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900'
          } ${triggerClassName || ''}`}
      >
        { triggerIcon && <span className='shrink-0'>{ triggerIcon }</span> }
        <span>{ triggerLabel }</span>
        <ChevronDownIcons
            className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-slate-600' : ''
            }`}
        />
      </button>

      { isOpen &&
        createPortal(
          <div
              ref={menuRef}
              className='fixed z-[9999] w-[250px] rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/80 shadow-[0_16px_36px_-6px_rgba(0,0,0,0.14),0_0_0_1px_rgba(0,0,0,0.04)] overflow-hidden transition-all duration-150 animate-in fade-in zoom-in-95'
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
              }}
          >
            <div className='px-3 pt-2.5 pb-1 border-b border-slate-100 flex items-center justify-between'>
              <span className='text-[10px] font-bold uppercase tracking-wider text-slate-400'>
                Move Candidate
              </span>
              <span className='text-[10px] text-slate-400 font-medium'>
                Select state
              </span>
            </div>
            <div
                className='p-1.5 space-y-0.5 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300/80 [&::-webkit-scrollbar-track]:bg-transparent'
                style={{
                  maxHeight: `${position.maxHeight - 38}px`,
                }}
            >
              { options.map((option) => {
                const badge = getStatusBadge(option);
                return (
                  <button
                      key={option}
                      type='button'
                      onClick={() => {
                        onSelect(option);
                        onToggle();
                      }}
                      className='flex items-center justify-between w-full px-2.5 py-2 text-xs font-medium text-slate-700 hover:text-slate-950 hover:bg-slate-100/90 active:bg-slate-200/80 rounded-xl transition-all duration-150 group text-left'
                  >
                    <div className='flex items-center gap-2.5 min-w-0'>
                      <span
                          className={`w-2 h-2 rounded-full ring-4 shrink-0 transition-transform group-hover:scale-110 ${badge.dot}`}
                      />
                      <span className='truncate text-slate-700 group-hover:text-slate-900 font-medium'>
                        Move to{ ' ' }
                        <span className='font-semibold text-slate-900'>
                          { option }
                        </span>
                      </span>
                    </div>
                    <span className='text-slate-400 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0 font-bold'>
                      →
                    </span>
                  </button>
                );
              }) }
            </div>
          </div>,
          document.body,
        ) }
    </>
  );
};

